import { fileToDataUrl, useCallback, useState } from "@";
import { addGiftProjectAttribute } from "./helpers";

// Hook for managing gift card design
export const useGifCardDesign = (getValues, setValue) => {
  const [giftAttributeDesigner, setGiftAttributeDesigner] = useState({});
  const [isShowGifCardDesign, setIsShowGifCardDesign] = useState(false);

  // Save the gift card design
  const saveGifCardDesign = useCallback(
    (designs) => {
      const { index } = giftAttributeDesigner;
      setValue(`attributes.${index}.giftingCardDesigns`, designs);
      setIsShowGifCardDesign(false);
      document.body.style.overflow = "auto";
    },
    [setValue, giftAttributeDesigner, setIsShowGifCardDesign],
  );

  // Show the gift card designer
  const showGifCardDesigner = useCallback(
    (index, edit) => {
      if (edit) {
        const attribute = { ...getValues("attributes")[index], index };
        setGiftAttributeDesigner(attribute);
        setIsShowGifCardDesign(true);
        document.body.style.overflow = "hidden";
      }
    },
    [getValues, setIsShowGifCardDesign, setGiftAttributeDesigner],
  );

  return {
    saveGifCardDesign,
    showGifCardDesigner,
    isShowGifCardDesign,
    giftAttributeDesigner,
    setIsShowGifCardDesign,
  };
};

// Hook for managing gift attributes
export const useGiftAttributes = (getValues, setValue) => {
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [giftAttribute, setGiftAttribute] = useState({});

  // Reset attribute fields
  const resetAttributeFields = useCallback(() => {
    setValue("attributeTitle", "");
    setValue("attributeAmount", "");
    setValue("attributeImage", "");
    setValue("attributeGiftingCardDesigns", []);
  }, [setValue]);

  // Add a new attribute
  const addAttribute = useCallback(async () => {
    const title = getValues("attributeTitle");
    const image = getValues("attributeImage");
    const amount = getValues("attributeAmount");
    const attributes = getValues("attributes") || [];

    const updatedAttributes = await addGiftProjectAttribute(
      title,
      amount,
      image,
      attributes,
    );
    setValue("attributes", updatedAttributes);
    resetAttributeFields();
  }, [getValues, setValue, resetAttributeFields]);

  // Edit an existing attribute
  const editAttribute = useCallback(
    (item, index) => {
      setIsEdit(true);
      setEditIndex(index);
      setGiftAttribute(item);
      setValue("attributeId", item.id);
      setValue("attributeTitle", item.title);
      setValue("attributeAmount", item.amount);
    },
    [setIsEdit, setEditIndex, setGiftAttribute, setValue],
  );

  // Cancel editing an attribute
  const cancelEditAttribute = useCallback(() => {
    setIsEdit(false);
    setEditIndex(null);
    setGiftAttribute({});
    resetAttributeFields();
  }, [resetAttributeFields]);

  // Save the edited attribute
  const saveEditAttribute = useCallback(async () => {
    const attributes = getValues("attributes");
    const imageFile = getValues("attributeImage")?.target?.files[0];
    const imageUrl = imageFile ? await fileToDataUrl(imageFile) : "";

    attributes[editIndex] = {
      image: imageFile,
      imageView: imageUrl,
      id: getValues("attributeId"),
      title: getValues("attributeTitle"),
      amount: getValues("attributeAmount"),
      imageFullUrl: giftAttribute?.imageFullUrl,
      giftingCardDesigns: giftAttribute?.giftingCardDesigns,
    };
    setValue("attributes", attributes);
    setIsEdit(false);
    setEditIndex(null);
    setGiftAttribute({});
    resetAttributeFields();
  }, [
    getValues,
    setValue,
    editIndex,
    resetAttributeFields,
    giftAttribute?.imageFullUrl,
    giftAttribute?.giftingCardDesigns,
  ]);

  return {
    isEdit,
    editIndex,
    addAttribute,
    editAttribute,
    saveEditAttribute,
    cancelEditAttribute,
    resetAttributeFields,
  };
};
